import { IPaymentPlan } from "shared";
import { useState } from "react";

import { useInvoice } from "../../../hooks/useInvoice";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { Button } from "../../../../common/Atoms/Button";
import { Modal } from "../../../../common/Atoms/Modal";
import { useDeletePaymentPlanMutation } from "../../../services/api/invoiceApi/invoice";
import { Notification } from "../../../../common/Atoms/Notification";
import { InvoicePaymentPlanDisplay } from "../../../../common/Components/PaymentPlan/InvoicePaymentPlanDisplay";
import { Card } from "../../../../common/Atoms/Card";
import { LoadingOverlay } from "../../Molecules/LoadingOverlay";

export function InvoicePaymentPlan() {
  const { invoice, isLoading } = useInvoice();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const plans = (invoice?.paymentPlans as IPaymentPlan[] | undefined) || [];

  if (plans.length > 1) {
    // Should never happen, but log loudly if it does
    console.error(`InvoicePaymentPlan: more than one payment plan found for invoice ${invoice?.id}`, {
      invoiceId: invoice?.id,
      planIds: plans.map(p => p.id),
    });
  }

  const plan = plans[0];

  if (!plan) {
    return null;
  }

  if (!invoice) {
    return null;
  }

  return (
    <>
      <Card className={ `mb-6` }>
        <LoadingOverlay loading={ isLoading }>
          <InvoicePaymentPlanDisplay
            invoiceCurrency={ invoice.xero_currency }
            plan={ plan }
            onRemovePlan={ () => setConfirmOpen(true) }
            timezone={ invoice.organisationTimezone }
          />

          { /* If the data is there show the user created meta data */ }
          <Paragraph variant={ `secondary` }
            className={ `text-sm` }>
            { `Plan created by ${ plan.createdByUserReference || `Unknown User` } on ` }

            <time dateTime={ new Date(plan.createdAt).toDateString() || `` }>
              { new Date(plan.createdAt || ``).toLocaleDateString(
                undefined,
                {
                  year: `numeric`,
                  month: `short`,
                  day: `numeric`,
                  hour: `2-digit`,
                  minute: `2-digit`,
                },
              ) }
            </time>

          </Paragraph>
        </LoadingOverlay>
      </Card>

      <RemovePaymentPlanModal
        open={ confirmOpen }
        setOpen={ setConfirmOpen }
        plan={ plan }
      />
    </>
  );
}

interface RemovePaymentPlanModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  plan: IPaymentPlan;
}

/** Confirms and deletes the invoice's payment plan */
export function RemovePaymentPlanModal({ open, setOpen, plan }: RemovePaymentPlanModalProps) {
  const { invoice } = useInvoice();
  const [deletePaymentPlan, { isLoading: deleteLoading }] = useDeletePaymentPlanMutation();

  return (
    <Modal
      open={ open }
      setOpen={ setOpen }
      title={ `Remove Payment Plan` }
    >
      <Paragraph className={ `mb-4` }>
        { `Are you sure you want to remove this payment plan? This will delete all installments.` }
      </Paragraph>
      <Notification
        type={ `warning` }
        className={ `mb-4` }>
        { `You will be unable to start a new payment plan on any invoices that already have payments.` }
      </Notification>
      <div className={ `flex justify-end gap-2` }>
        <Button
          color={ `gray` }
          size={ `md` }
          onClick={ () => setOpen(false) }
        >
          { `Cancel` }
        </Button>
        <Button
          color={ `red` }
          size={ `md` }
          loading={ deleteLoading }
          onClick={ async() => {
            await deletePaymentPlan({
              organisationId: invoice.organisation_id,
              invoiceId: invoice.id,
              paymentPlanId: plan.id,
            });

            setOpen(false);
          } }
        >
          { `Remove` }
        </Button>
      </div>
    </Modal>
  );
}
